import React, { useEffect, useState } from "react";
import { Users, Loader2, ChevronDown } from "lucide-react";
import { codexApi } from "../lib/api";

/**
 * "Witness Testimony" — on-demand panel. Stays collapsed by default; the
 * user clicks "Show witness testimony" to fetch.
 *
 * Saves LLM cost: the Compare endpoint runs an LLM relevance judge per
 * query. Auto-firing on every OS answer was paying that cost even when
 * the user didn't read the panel. Now we only fire on explicit user
 * intent.
 *
 * "By the mouth of two or three witnesses every word shall be established"
 *   — Deuteronomy 19:15 / 2 Corinthians 13:1
 */
export default function CompareWitnesses({ topic }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [expanded, setExpanded] = useState(false);

    // Reset state if the topic itself changes (new OS answer in a thread).
    useEffect(() => {
        setExpanded(false);
        setData(null);
        setLoading(false);
    }, [topic]);

    const handleExpand = async () => {
        if (!topic || !topic.trim()) return;
        if (data) {
            setExpanded(true);
            return;
        }
        setExpanded(true);
        setLoading(true);
        try {
            const res = await codexApi.compareWitnesses(topic);
            setData(res);
        } catch (e) {
            setData({ topic, witnesses: [] });
        } finally {
            setLoading(false);
        }
    };

    if (!topic || !topic.trim()) return null;

    if (!expanded) {
        return (
            <div
                className="mt-5 border-t border-[#D4AF37]/20 pt-4"
                data-testid="compare-witnesses-block"
            >
                <button
                    type="button"
                    onClick={handleExpand}
                    className="group flex items-center gap-2.5 px-1 py-1 text-left hover:opacity-90 transition-opacity"
                    data-testid="show-witnesses-button"
                >
                    <Users size={16} className="text-[#D4AF37] flex-shrink-0" />
                    <span className="font-mono-os text-[11px] sm:text-sm tracking-[0.22em] sm:tracking-[0.28em] uppercase text-[#F9E596]">
                        Show witness testimony
                    </span>
                    <ChevronDown
                        size={15}
                        className="text-[#D4AF37] transition-transform group-hover:translate-y-0.5"
                    />
                </button>
                <p className="text-[11px] text-[#94A3B8] italic mt-1.5 px-1 leading-snug">
                    Optional: pull what 1 Enoch · Jubilees · the Apocrypha ·
                    pre-Nicene Fathers · and scholarly research say on this
                    topic.
                </p>
            </div>
        );
    }

    return (
        <div
            className="mt-5 border-t border-[#D4AF37]/20 pt-4"
            data-testid="compare-witnesses-block"
        >
            <div className="flex items-center gap-2 mb-3 px-0.5 flex-wrap">
                <Users size={16} className="text-[#D4AF37] flex-shrink-0" />
                <span
                    className="font-mono-os text-[11px] sm:text-sm tracking-[0.22em] sm:tracking-[0.28em] uppercase text-[#F9E596]"
                    data-testid="witness-testimony-header"
                >
                    Witness Testimony
                </span>
                <span className="hidden md:inline text-xs text-[#94A3B8]">
                    · 1 Enoch · Jubilees · Apocrypha · Pre-Nicene Fathers · Scholarly
                </span>
            </div>

            <div
                className="max-h-[420px] overflow-y-auto space-y-3 rounded-lg bg-[#0A1220]/35 border border-[#D4AF37]/15 p-2.5 sm:p-3"
                data-testid="compare-witnesses-panel"
            >
                {loading && (
                    <p
                        className="font-mono-os text-[11px] sm:text-xs tracking-[0.32em] uppercase text-[#D4AF37] flex items-center gap-2 px-1 py-2"
                        data-testid="compare-witnesses-loading"
                    >
                        <Loader2 size={14} className="animate-spin" />
                        Gathering the council
                    </p>
                )}
                {!loading && data && data.witnesses.length === 0 && (
                    <p className="text-[#CBD5E1] text-sm sm:text-base px-1 py-2">
                        No extra-canonical witness in the indexed council
                        speaks to this directly. Scripture, as cited in the
                        main answer above, is the standing witness.
                    </p>
                )}
                {!loading && data && data.witnesses.length > 0 && (
                    <ol className="space-y-2.5 sm:space-y-3">
                        {data.witnesses.map((w, i) => (
                            <li
                                key={`${w.work}-${w.ref}-${i}`}
                                className="bg-[#0A1220]/55 border border-[#D4AF37]/20 rounded-lg px-3 sm:px-4 py-3 sm:py-3.5"
                                data-testid={`compare-witness-${i}`}
                            >
                                <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-0.5 sm:gap-3 mb-1.5">
                                    <span className="font-serif-display text-base sm:text-lg text-[#F9E596] leading-tight break-words">
                                        {w.work}
                                    </span>
                                    <span className="font-mono-os text-[10px] sm:text-xs tracking-[0.18em] sm:tracking-[0.22em] uppercase text-[#D4AF37] sm:flex-shrink-0 break-all">
                                        {w.ref}
                                    </span>
                                </div>
                                <p className="text-[#E2E8F0] text-sm sm:text-base leading-relaxed italic break-words">
                                    &ldquo;{w.quote}&rdquo;
                                </p>
                            </li>
                        ))}
                    </ol>
                )}
            </div>

            {!loading && data && data.witnesses.length > 0 && (
                <p className="text-[11px] sm:text-xs text-[#94A3B8] italic px-1 mt-2 leading-snug">
                    Witnesses cited from the indexed council. They stand
                    under Scripture, never above it (Deuteronomy 19:15 /
                    2 Corinthians 13:1).
                </p>
            )}
        </div>
    );
}
